import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react'

import { ErrorResponse } from '@cash-web/data-access-cash-app-pay-api-types'

import { isApiError } from './guards'

export interface RequestOptions {
  headers?: Record<string, string>
  credentials?: RequestCredentials
}

export class FetchServiceError extends Error {
  readonly status: number
  readonly data: unknown

  constructor(status: number, data: unknown) {
    super(`Request failed with status ${status}`)
    this.name = 'FetchServiceError'
    this.status = status
    this.data = data
  }
}

export class HttpService {
  async request<Req, Resp>(
    url: string,
    method: string,
    data: Req,
    options?: RequestOptions
  ): Promise<Resp | undefined> {
    const body = data != null ? JSON.stringify(data) : undefined

    const init: RequestInit = {
      method,
      credentials: options?.credentials ?? 'include',
      headers: {
        Accept: 'application/json',
        ...(body != null && { 'Content-Type': 'application/json' }),
        ...options?.headers,
      },
      ...(body != null && { body }),
    }

    const response = await fetch(url, init)

    if (!response.ok) {
      const responseBody = await response.json().catch(() => null)
      throw new FetchServiceError(response.status, responseBody)
    }

    const text = await response.text()
    if (!text) return undefined
    return JSON.parse(text) as Resp
  }
}

type BaseQueryArgs = {
  url: string
  method: string
  data?: unknown
}

export enum BaseQueryErrorTypes {
  FETCH_ERROR,
  API_ERROR,
}

export type BaseFetchError = {
  /**
   * * `"FETCH_ERROR"`:
   *   An error that occurred during execution of `fetch` or the `fetchFn` callback option
   **/
  type: BaseQueryErrorTypes.FETCH_ERROR
  error: string
}

export type BaseApiError = {
  /**
   * * `"API_ERROR"`:
   *   An error that occurred during execution of `fetch` or the `fetchFn` callback option
   **/
  type: BaseQueryErrorTypes.API_ERROR
  data: ErrorResponse[]
  status: number
}

export class ApiService {
  public baseQueryFn: HttpService | null = null

  readonly getBaseQuery: BaseQueryFn<BaseQueryArgs, unknown, BaseFetchError | BaseApiError> = async args => {
    const { url, method, data } = args
    if (this.baseQueryFn === null) {
      throw new Error('baseQueryFn not set')
    }

    try {
      const result = await this.baseQueryFn.request(url, method, data ?? null)
      return { data: result, error: undefined }
    } catch (e) {
      if (e instanceof FetchServiceError && isApiError(e.data)) {
        return { error: { type: BaseQueryErrorTypes.API_ERROR, status: e.status, data: e.data.errors } }
      }
      const error = e as Error
      return { error: { type: BaseQueryErrorTypes.FETCH_ERROR, error: error.message } }
    }
  }

  public setBaseQueryFn(queryFn: HttpService): ApiService {
    this.baseQueryFn = queryFn
    return this
  }

  readonly service = createApi({
    reducerPath: 'api',
    baseQuery: this.getBaseQuery,
    endpoints: () => ({}),
    tagTypes: ['Order', 'CustomerRequest'],
  })
}

export const apiService = new ApiService()
